import React, { useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import AccessDenied from './grids/accessDeniedGrid';
import { ROLE_HIERARCHY } from './utils/roleUtils';

type ProtectedRouteProps = {
  children: React.ReactNode;
  allowedRoles?: string[];
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, isLoading, loginWithRedirect, user } = useAuth0();
  const location = useLocation();

  const hasAccess = useMemo(() => {
    if (allowedRoles.length === 0) {
      return true;
    }

    const userRoles: string[] = user?.['https://odp.golf/roles'] || [];
    if (userRoles.length === 0) {
      return false;
    }

    // allowedRoles are treated as a minimum access tier. Example:
    // allowedRoles=["Admin"] -> users with Admin or Super are allowed.
    const acceptableUserRoles = Object.keys(ROLE_HIERARCHY).filter((role) =>
      allowedRoles.some((allowedRole) => (ROLE_HIERARCHY[role] || []).includes(allowedRole)),
    );

    return userRoles.some((userRole) => acceptableUserRoles.includes(userRole));
  }, [allowedRoles, user]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Stash the URL the user was trying to reach so onRedirectCallback in
      // index.tsx can bring them back here after Auth0 returns.
      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      loginWithRedirect({ appState: { returnTo } });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect, location.pathname, location.search, location.hash]);

  if (isLoading || !isAuthenticated) {
    return <div>Loading...</div>;
  }

  if (!hasAccess) {
    return <AccessDenied />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;



